import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2Icon, AlertTriangleIcon } from 'lucide-react';
import { landingPagesApi, LandingPageRecord } from '../services/creatorApi';
type PageState = 'loading' | 'ready' | 'not_found' | 'error';
function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function buildFullDocument(record: {
  Page_Name?: string;
  HTML?: string;
  CSS?: string;
  JavaScript1?: string;
}): string {
  var css = record.CSS || '';
  var js = record.JavaScript1 || '';
  var html = record.HTML || '';
  var styleOpen = '<' + 'style>';
  var styleClose = '</' + 'style>';
  var scriptOpen = '<' + 'script>';
  var scriptClose = '</' + 'script>';
  var parts: string[] = [];
  parts.push('<!DOCTYPE html>');
  parts.push('<html lang="en">');
  parts.push('<head>');
  parts.push('<meta charset="utf-8">');
  parts.push('<meta name="viewport" content="width=device-width, initial-scale=1">');
  if (record.Page_Name) {
    parts.push('<title>' + escapeHtml(record.Page_Name) + '</title>');
  }
  parts.push(styleOpen);
  parts.push('*, *::before, *::after { box-sizing: border-box; }');
  parts.push('body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }');
  if (css) {
    parts.push(css);
  }
  parts.push(styleClose);
  parts.push('</head>');
  parts.push('<body>');
  parts.push(html);
  if (js) {
    parts.push(scriptOpen + js + scriptClose);
  }
  parts.push('</body>');
  parts.push('</html>');
  return parts.join('\n');
}
export function PublicLandingPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [state, setState] = useState<PageState>('loading');
  const [page, setPage] = useState<any>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (!slug) {
      setState('not_found');
      return;
    }
    const loadPage = async () => {
      setState('loading');
      try {
        const record = await landingPagesApi.findBySlug(slug);
        if (!record) {
          setState('not_found');
          return;
        }
        if (record.Status?.toLowerCase() !== 'active') {
          setState('not_found');
          return;
        }
        setPage(record);
        setState('ready');
      } catch (err) {
        console.error('Failed to load landing page:', err);
        setState('error');
      }
    };
    loadPage();
  }, [slug]);
  if (state === 'loading') {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-5681" data-mp="3in0hmz19illy">
        <div className="text-center" data-id="element-5682" data-mp="34vdo36ypfr6v">
          <Loader2Icon className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-3" data-id="element-5683" data-mp="38uuuq3pvaudm" />
          <p className="text-sm text-gray-500" data-id="element-5684" data-mp="38srs8k7j38s0">Loading...</p>
        </div>
      </div>;
  }
  if (state === 'not_found') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-5685" data-mp="2zupnbtkobvzg">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center max-w-md w-full" data-id="element-5686" data-mp="2g84j1q9whncg">
          <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-6" data-id="element-5687" data-mp="2bacqcu1flx77">
            <AlertTriangleIcon className="h-8 w-8 text-amber-500" data-id="element-5688" data-mp="2q7vim6dbisje" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5689" data-mp="2l7hh77682guc">
            Page not found
          </h1>
          <p className="text-gray-500 text-sm" data-id="element-5690" data-mp="2z6gw3n3xnhdb">
            The page you are looking for does not exist or is no longer
            available.
          </p>
        </div>
      </div>;
  }
  if (state === 'error') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-5691" data-mp="38wjuy3xqjpjk">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center max-w-md w-full" data-id="element-5692" data-mp="33yqttpbahlq5">
          <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6" data-id="element-5693" data-mp="3hy9r1n2kbcno">
            <AlertTriangleIcon className="h-8 w-8 text-red-500" data-id="element-5694" data-mp="3dubqr91i3vt1" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5695" data-mp="1b97xbcu7jbf9">
            Something went wrong
          </h1>
          <p className="text-gray-500 text-sm mb-4" data-id="element-5696" data-mp="1g6rcceyqbs89">
            We could not load this page right now.
          </p>
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-5697" data-mp="1pynmioe3b8su">
            Try Again
          </button>
        </div>
      </div>;
  }
  if (page) {
    var doc = buildFullDocument(page);
    return <iframe ref={iframeRef} srcDoc={doc} title={page.Page_Name || 'Landing Page'} className="w-full border-0" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none'
    }} sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-id="element-5698" data-mp="12b5tkpj3a4oa" />;
  }
  return null;
}